import { Box, Table, Text, Link } from '@workwhile/ui'
import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import type { AdminEventLogItem } from 'api/admin/eventLogs/listEventLogs'
import { formatInTimeZone } from 'date-fns-tz'
import { getLocalTimezone } from 'lib/time'

type IdAccessor = keyof Pick<
  AdminEventLogItem,
  'userId' | 'subjectUserId' | 'companyId' | 'shiftId'
>

const idLinkColumn = (
  header: string,
  accessor: IdAccessor,
  routePrefix: string,
  size = 90
): ColumnDef<AdminEventLogItem> => ({
  header,
  accessorKey: accessor,
  size,
  cell: ({ row }) => {
    const id = row.original[accessor]
    if (id == null) return '—'
    return <Link to={`${routePrefix}/${id}`}>{id}</Link>
  },
})

const columns: ColumnDef<AdminEventLogItem>[] = [
  {
    header: 'Time',
    accessorKey: 'timestamp',
    size: 200,
    cell: ({ row }) => (
      <Text fontSize="sm">
        {formatInTimeZone(
          row.original.timestamp,
          getLocalTimezone(),
          'yyyy-MM-dd HH:mm:ss zzz'
        )}
      </Text>
    ),
  },
  {
    header: 'Type',
    accessorKey: 'eventType',
    size: 200,
    cell: ({ row }) => <Text fontSize="sm">{row.original.eventType}</Text>,
  },
  {
    header: 'Message',
    accessorKey: 'message',
    minSize: 240,
    cell: ({ row }) => (
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {row.original.message}
      </Text>
    ),
  },
  idLinkColumn('User', 'userId', '/users'),
  idLinkColumn('Subject User', 'subjectUserId', '/users'),
  idLinkColumn('Company', 'companyId', '/companies', 100),
  idLinkColumn('Shift', 'shiftId', '/shifts'),
]

type EventLogsTableProps = {
  items: AdminEventLogItem[]
}

export const EventLogsTable = ({ items }: EventLogsTableProps) => {
  return (
    <Box overflowX="auto">
      <Table columns={columns} data={items} textVerticalAlign="top" />
    </Box>
  )
}
